// src/components/document/ComplianceDashboard.tsx
// QMS/Compliance overview cards for formal document mode.
// Shows key metrics: published count, expiring, overdue, acknowledgment rate.

import { useI18n } from '@react/hooks';
import type { ComplianceStats } from '@/features/document/types';

interface ComplianceDashboardProps {
  stats: ComplianceStats;
}

function StatCard({
  labelKey,
  value,
  variant,
  t,
  subtitle,
}: {
  labelKey: string;
  value: string | number;
  variant: 'default' | 'success' | 'warning' | 'error';
  t: (k: string) => string;
  subtitle?: string;
}) {
  const variantClasses: Record<string, string> = {
    default: 'border-border',
    success: 'border-l-emerald-500 border-l-2',
    warning: 'border-l-yellow-500 border-l-2',
    error: 'border-l-destructive border-l-2',
  };

  const valueColors: Record<string, string> = {
    default: 'text-foreground',
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    error: 'text-destructive',
  };

  return (
    <div className={`rounded-lg border bg-card p-4 ${variantClasses[variant]}`}>
      <p className="text-xs text-muted-foreground mb-1">{t(labelKey)}</p>
      <p className={`text-2xl font-bold ${valueColors[variant]}`}>{value}</p>
      {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

export function ComplianceDashboard({ stats }: ComplianceDashboardProps) {
  const { t } = useI18n();

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-4">
      <StatCard
        labelKey="document.compliance.published"
        value={stats.published}
        variant="success"
        t={t}
        subtitle={t('document.compliance.ofTotal', { total: stats.total })}
      />
      <StatCard
        labelKey="document.compliance.expiringSoon"
        value={stats.expiringSoon}
        variant={stats.expiringSoon > 0 ? 'warning' : 'default'}
        t={t}
        subtitle={t('document.compliance.within30days')}
      />
      <StatCard
        labelKey="document.compliance.overdue"
        value={stats.overdue}
        variant={stats.overdue > 0 ? 'error' : 'default'}
        t={t}
      />
      <StatCard
        labelKey="document.compliance.acknowledgmentRate"
        value={`${stats.acknowledgedPercent}%`}
        variant={stats.acknowledgedPercent >= 90 ? 'success' : stats.acknowledgedPercent >= 70 ? 'warning' : 'error'}
        t={t}
        subtitle={stats.pendingAcknowledgment > 0
          ? t('document.compliance.pendingAck', { count: stats.pendingAcknowledgment })
          : undefined
        }
      />
    </div>
  );
}
